/**
 * `extractBook` — the ONE extraction pipeline. Import and the ghost probe
 * run through it; reprocess shares its `extractPreamble` + `mapChapters`.
 *
 * Pure module: no store imports — `ExtractionOptions` is passed in by the
 * caller (the orchestrator captures it per job), never read from a store.
 * `depth: 'metadata'` short-circuits after one epubjs open (the ghost probe
 * does not pay cover compression/palette extraction twice per import —
 * pass the probe result back via `preamble`). `signal` aborts between
 * chapters in the offscreen render loop (the cancellable-task-runner
 * pattern); abort surfaces as `CancellationError`.
 */
// First-use splitting: epubjs and the offscreen renderer load
// lazily at the extraction call sites — this module rides the eager
// LibraryView graph (via the ImportOrchestrator), and a static import here
// would put epubjs back into the entry chunk (check 4 of
// scripts/check-worker-chunk.mjs asserts the emitted artifact).
import { v4 as uuidv4 } from 'uuid';
import type { NavigationItem, SectionMetadata, StaticBookManifest, StaticResource, PerceptualPalette } from '~types/book';
import type { UserInventoryItem, UserProgress, UserOverrides, ReadingListEntry } from '~types/user-data';
import type { CacheTtsPreparation, TableImage } from '~types/cache';
import { TTS_EXTRACTION_VERSION, type ExtractionOptions } from '@lib/ingestion/sentence-extraction';
import type { ProcessedChapter } from '@domains/reader/engine/offscreen/offscreen-renderer';
import { CancellationError } from '@lib/cancellable-task-runner';
import { CURRENT_BOOK_VERSION } from '@lib/constants';
import { extractCoverPalette } from '@lib/cover-palette';
import { createLogger } from '@lib/logger';
import { normalizeLanguageCode } from '@lib/language-utils';
import { localFetch } from '@kernel/net';
import { measureSince } from '@lib/perf';
import { cheapHash, computeContentHash, computeLegacyFingerprint } from './identity';
import { getSanitizedBookMetadata } from './metadata';
import { validateZipSignature } from './validate';
import { archiveOfEpubJsBook } from './epubArchive';
// Type-only: the structure resolvers load lazily at first import (like
// epubjs below) — this module rides the eager LibraryView graph.
import type { BookStructure } from '@lib/epub/structure/bookStructure';
import { isPlaceholderTitle } from '@lib/epub/structure/copyrightPage';

const logger = createLogger('Ingestion');

type ExtractDepth = 'metadata' | 'full';

/** One spine item's plain text — the persisted search corpus (`searchText`). */
interface SearchTextSection {
  href: string;
  title: string;
  text: string;
  /**
   * Invalidation/skip key for the embedding indexer: a cheapHash of the
   * section text stamped at import. The indexer resumes on the
   * `{href, sectionTextHash}` pair (plan/semantic-search-design.md §2.1).
   * Optional for backward compatibility with corpora extracted before this
   * field existed.
   */
  sectionTextHash?: string;
}

/** The extractor's search output; persisted per book by the searchText repo. */
export interface BookSearchText {
  /** Invalidation stamp: re-extract when below the current extraction version. */
  extractionVersion: number;
  sections: SearchTextSection[];
}

/** Shared (both depths) extraction output. */
export interface BookMetadataExtraction {
  depth: 'metadata';
  title: string;
  author: string;
  description: string;
  /** Normalized ISO 639-1 (defaults to 'en'). */
  language: string;
  /** Compressed thumbnail when compression succeeded, else the original cover. */
  coverBlob?: Blob;
  coverPalette?: number[];
  perceptualPalette?: PerceptualPalette;
  /** SHA-256 over content bytes (see identity.ts). */
  contentHash: string;
  /** Legacy filename-embedding fingerprint, still written for backward compatibility (see identity.ts). */
  legacyFingerprint: string;
  /** The publisher TOC (may be empty; full depth falls back to a synthetic TOC). */
  toc: NavigationItem[];
}

/** Full-depth extraction output — everything the register stage persists. */
export interface FullBookExtraction extends Omit<BookMetadataExtraction, 'depth'> {
  depth: 'full';
  bookId: string;
  manifest: StaticBookManifest;
  resource: StaticResource;
  structure: {
    bookId: string;
    toc: NavigationItem[];
    spineItems: { id: string; characterCount: number; index: number }[];
  };
  sections: SectionMetadata[];
  /** The ONE inventory producer — registration consumes this (carries perceptualPalette + language). */
  inventory: UserInventoryItem;
  progress: UserProgress;
  overrides: UserOverrides;
  readingListEntry: ReadingListEntry;
  ttsContentBatches: CacheTtsPreparation[];
  tableBatches: TableImage[];
  searchText: BookSearchText;
}

export type BookExtraction = BookMetadataExtraction | FullBookExtraction;

export interface ExtractBookOptions {
  depth: ExtractDepth;
  /** Segmentation/sanitization options, captured per job by the orchestrator. */
  extraction?: ExtractionOptions;
  /** Aborts between chapters in the offscreen render loop (CancellationError). */
  signal?: AbortSignal;
  onProgress?: (progress: number, message: string) => void;
  /**
   * A previously-extracted metadata-depth result for the SAME file (the
   * ghost-probe output): the full pass reuses it and skips the
   * open/cover/palette/hash preamble entirely.
   */
  preamble?: BookMetadataExtraction;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new CancellationError('Extraction cancelled');
}

// ── The preamble: one epubjs open for metadata/cover/palette/TOC ──────────

interface PreambleOptions {
  /** 'thumbnail' compresses the cover before palette extraction (import); 'raw' uses the original (reprocess parity). */
  cover: 'thumbnail' | 'raw';
  signal?: AbortSignal;
}

export interface BookPreamble {
  /** Raw (unsanitized) OPF metadata — the legacy fingerprint hashes these. */
  rawTitle: string;
  rawAuthor: string;
  /**
   * Display title/author (unsanitized): the OPF's, or the copyright page's
   * when the OPF has no creator or only a file-name title. Never hashed —
   * the fingerprint must keep matching books imported before the fallback.
   */
  title: string;
  author: string;
  rawDescription: string;
  language: string;
  coverBlob?: Blob;
  coverPalette?: number[];
  perceptualPalette?: PerceptualPalette;
  toc: NavigationItem[];
}

/** Open the EPUB once and pull metadata, cover (+palette) and the publisher TOC. */
export async function extractPreamble(file: Blob, options: PreambleOptions): Promise<BookPreamble> {
  throwIfAborted(options.signal);

  const { default: ePub } = await import('epubjs');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const book = (ePub as any)(file, { replacements: 'none' });
  try {
    await book.ready;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadata = await (book.loaded as any).metadata;
    const language = normalizeLanguageCode(metadata.language || metadata.lang);

    // The package's own structure (OPF + nav + NCX), read through the zip
    // epub.js already holds — drives TOC resolution and cover finding for
    // malformed books (plan/epub-toc-cover-hardening.md). `null` only for a
    // non-archived book, where epub.js's own answers are used unchanged.
    const archive = archiveOfEpubJsBook(book);
    const {
      readBookStructure,
      resolveStructureCover,
      resolveStructureToc,
      resolveForeignToc,
      resolveStructureCopyright,
    } = await import('@lib/epub/structure/bookStructure');
    let structure: BookStructure | null = null;
    if (archive) {
      try {
        structure = await readBookStructure(archive.port, archive.opfPath);
      } catch (error) {
        logger.warn('Failed to read package structure, using epub.js defaults:', error);
      }
    }

    let coverBlob: Blob | undefined;
    let thumbnailBlob: Blob | undefined;
    let coverPalette: number[] | undefined;
    let perceptualPalette: PerceptualPalette | undefined;

    if (structure && archive) {
      const cover = await resolveStructureCover(structure, archive.port);
      if (cover.pick) {
        coverBlob = cover.pick.blob;
        logger.info(`Cover: ${cover.pick.candidate.href} (${cover.pick.candidate.reason})`);
      }
      for (const { candidate, why } of cover.rejected) {
        logger.debug(`Cover candidate skipped: ${candidate.href} (${candidate.reason}) — ${why}`);
      }
    } else {
      const coverUrl = await book.coverUrl();
      if (coverUrl) {
        try {
          const response = await localFetch(coverUrl);
          coverBlob = await response.blob();
        } catch (error) {
          logger.warn('Failed to retrieve cover blob:', error);
        }
      }
    }

    if (coverBlob && options.cover === 'thumbnail') {
      try {
        // Lazy: browser-image-compression (~55KB min) is only needed at
        // import time; a static import would ride the eager LibraryView
        // graph into the entry chunk (parsed on every boot).
        const { default: imageCompression } = await import('browser-image-compression');
        thumbnailBlob = await imageCompression(coverBlob as File, {
          maxSizeMB: 0.1,
          maxWidthOrHeight: 600,
          useWebWorker: true,
          fileType: 'image/webp',
        });
      } catch (error) {
        logger.warn('Failed to compress cover image, using original:', error);
        thumbnailBlob = coverBlob;
      }
    }

    // Generate palette if we have any cover image. A palette failure never
    // aborts extraction; the book only loses the optional palette.
    if (thumbnailBlob || coverBlob) {
      try {
        const result = await extractCoverPalette((thumbnailBlob || coverBlob)!);
        coverPalette = result.palette;
        perceptualPalette = result.perceptualPalette;
        if (coverPalette.length === 0) coverPalette = undefined;
      } catch (error) {
        logger.warn('Failed to extract cover palette:', error);
      }
    }

    let toc: NavigationItem[];
    const choice = structure ? resolveStructureToc(structure) : null;
    if (choice?.source) {
      toc = choice.toc;
      logger.info(
        `TOC from ${choice.source}: ${choice.score?.resolved}/${choice.score?.total} entries resolve; ` +
          `junk labels ${Math.round(choice.junkRateBefore * 100)}% → ${Math.round(choice.junkRateAfter * 100)}%`,
      );
    } else {
      // No package structure, or neither nav nor NCX parsed into entries
      // here: epub.js's own parse, still resolved onto the spine if we can.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const navigation = await (book.loaded as any).navigation;
      const raw: NavigationItem[] = navigation?.toc ?? [];
      toc = structure && raw.length > 0 ? resolveForeignToc(structure, raw) : raw;
    }

    const rawTitle: string = metadata.title || 'Untitled';
    const rawAuthor: string = metadata.creator || 'Unknown Author';
    let title = rawTitle;
    let author = rawAuthor;
    const needsTitle = isPlaceholderTitle(metadata.title);
    const needsAuthor = !metadata.creator?.trim();
    if (structure && archive && (needsTitle || needsAuthor)) {
      try {
        const fallback = await resolveStructureCopyright(structure, archive.port, toc);
        if (needsTitle && fallback.title) title = fallback.title;
        if (needsAuthor && fallback.author) author = fallback.author;
        if (title !== rawTitle || author !== rawAuthor) {
          logger.info(`Metadata from copyright page: "${title}" by ${author}`);
        }
      } catch (error) {
        logger.warn('Failed to read copyright page metadata:', error);
      }
    }

    return {
      rawTitle,
      rawAuthor,
      title,
      author,
      rawDescription: metadata.description || '',
      language,
      coverBlob: thumbnailBlob || coverBlob,
      coverPalette,
      perceptualPalette,
      toc,
    };
  } finally {
    await book.opened.catch(() => {});
    book.destroy();
  }
}

// ── The chapter mapping loop (shared by extractBook and reprocess) ──

export interface ChapterMapping {
  syntheticToc: NavigationItem[];
  sections: SectionMetadata[];
  ttsContentBatches: CacheTtsPreparation[];
  tableBatches: TableImage[];
  searchSections: SearchTextSection[];
  totalChars: number;
  /**
   * Heading-derived chapter titles keyed by spine href — the last-resort
   * source for replacing bare "Chapter N" TOC labels (hardening plan §9.5).
   */
  chapterTitles: Map<string, string>;
}

function toCacheTtsPrep(bookId: string, chapter: ProcessedChapter): CacheTtsPreparation {
  return {
    id: `${bookId}-${chapter.href}`,
    bookId,
    sectionId: chapter.href,
    sentences: chapter.sentences,
    citationMarkers: chapter.citationMarkers?.length > 0 ? chapter.citationMarkers : undefined,
    extractionVersion: TTS_EXTRACTION_VERSION,
  };
}

/** Map rendered chapters to TOC/sections/TTS-prep/table/search rows. */
export function mapChapters(bookId: string, chapters: ProcessedChapter[]): ChapterMapping {
  const syntheticToc: NavigationItem[] = [];
  const sections: SectionMetadata[] = [];
  const ttsContentBatches: CacheTtsPreparation[] = [];
  const tableBatches: TableImage[] = [];
  const searchSections: SearchTextSection[] = [];
  const chapterTitles = new Map<string, string>();
  let totalChars = 0;

  chapters.forEach((chapter, i) => {
    const title = chapter.title || `Chapter ${i + 1}`;
    if (chapter.title) chapterTitles.set(chapter.href, chapter.title);

    syntheticToc.push({
      id: `syn-toc-${i}`,
      href: chapter.href,
      label: title,
    });

    sections.push({
      id: `${bookId}-${chapter.href}`,
      bookId,
      sectionId: chapter.href,
      characterCount: chapter.textContent.length,
      playOrder: i,
      title,
    });
    totalChars += chapter.textContent.length;

    if (chapter.sentences.length > 0) {
      ttsContentBatches.push(toCacheTtsPrep(bookId, chapter));
    }

    if (chapter.tables && chapter.tables.length > 0) {
      chapter.tables.forEach((table) => {
        tableBatches.push({
          id: `${bookId}-${table.cfi}`,
          bookId,
          sectionId: chapter.href,
          cfi: table.cfi,
          imageBlob: table.imageBlob,
        });
      });
    }

    // Stamp the embedding indexer's {href, sectionTextHash} skip key at import:
    // cheapHash lives in this same domain (identity.ts — no cross-domain
    // import), and the chunker stays free of any hash import.
    searchSections.push({
      href: chapter.href,
      title,
      text: chapter.textContent,
      sectionTextHash: cheapHash(new TextEncoder().encode(chapter.textContent).buffer),
    });
  });

  return { syntheticToc, sections, ttsContentBatches, tableBatches, searchSections, totalChars, chapterTitles };
}

// ── extractBook ────────────────────────────────────────────────────────────

export async function extractBook(
  file: File,
  opts: ExtractBookOptions & { depth: 'metadata' },
): Promise<BookMetadataExtraction>;
export async function extractBook(
  file: File,
  opts: ExtractBookOptions & { depth: 'full' },
): Promise<FullBookExtraction>;
export async function extractBook(file: File, opts: ExtractBookOptions): Promise<BookExtraction>;
export async function extractBook(file: File, opts: ExtractBookOptions): Promise<BookExtraction> {
  const { depth, extraction, signal, onProgress, preamble: reused } = opts;

  let shared: BookMetadataExtraction;
  if (reused) {
    shared = reused;
  } else {
    const isValid = await validateZipSignature(file);
    if (!isValid) {
      throw new Error('Invalid file format. File must be a valid EPUB (ZIP archive).');
    }

    const preambleStart = performance.now();
    const preamble = await extractPreamble(file, { cover: 'thumbnail', signal });
    measureSince('import:preamble', preambleStart);

    // Identity. The legacy fingerprint hashes the UNSANITIZED metadata —
    // existing manifests were written that way and restore acceptance must
    // keep matching them.
    const identityStart = performance.now();
    const legacyFingerprint = await computeLegacyFingerprint(file, {
      title: preamble.rawTitle,
      author: preamble.rawAuthor,
      filename: file.name,
    });
    const contentHash = await computeContentHash(file);
    measureSince('import:identity-hashes', identityStart);

    // Sanitize the metadata candidate (sanitize-at-ingest boundary).
    const candidateMetadata = {
      id: 'pending-book-id',
      title: preamble.title,
      author: preamble.author,
      description: preamble.rawDescription,
      addedAt: Date.now(),
    };
    const check = getSanitizedBookMetadata(candidateMetadata);
    if (check) {
      candidateMetadata.title = check.sanitized.title;
      candidateMetadata.author = check.sanitized.author;
      candidateMetadata.description = check.sanitized.description ?? '';
      if (check.wasModified) {
        logger.warn(`Metadata sanitized for "${candidateMetadata.title}":`, check.modifications);
      }
    }

    shared = {
      depth: 'metadata',
      title: candidateMetadata.title,
      author: candidateMetadata.author,
      description: candidateMetadata.description,
      language: preamble.language,
      coverBlob: preamble.coverBlob,
      coverPalette: preamble.coverPalette,
      perceptualPalette: preamble.perceptualPalette,
      contentHash,
      legacyFingerprint,
      toc: preamble.toc,
    };
  }

  if (depth === 'metadata') {
    return shared;
  }

  throwIfAborted(signal);

  const optionsWithLocale: ExtractionOptions = { ...extraction, locale: shared.language };
  const { extractContentOffscreen } = await import(
    '@domains/reader/engine/offscreen/offscreen-renderer'
  );
  const offscreenStart = performance.now();
  const { chapters, baseFontSize, baseLineHeight } = await extractContentOffscreen(
    file,
    optionsWithLocale,
    onProgress,
    signal,
  );
  measureSince('import:offscreen-extract', offscreenStart);

  const bookId = uuidv4();
  const mapStart = performance.now();
  const mapping = mapChapters(bookId, chapters);
  measureSince('import:map-chapters', mapStart);

  const manifest: StaticBookManifest = {
    bookId,
    title: shared.title,
    author: shared.author,
    description: shared.description,
    fileHash: shared.legacyFingerprint,
    contentHash: shared.contentHash,
    fileSize: file.size,
    totalChars: mapping.totalChars,
    schemaVersion: CURRENT_BOOK_VERSION,
    isbn: undefined,
    coverBlob: shared.coverBlob,
    coverPalette: shared.coverPalette,
    perceptualPalette: shared.perceptualPalette,
    language: shared.language,
    baseFontSize,
    baseLineHeight,
  };

  const now = Date.now();
  const { repairTocLabels } = await import('@lib/epub/structure/tocLabels');

  return {
    ...shared,
    depth: 'full',
    bookId,
    manifest,
    resource: { bookId, epubBlob: file },
    structure: {
      bookId,
      toc: shared.toc.length > 0 ? repairTocLabels(shared.toc, [], mapping.chapterTitles) : mapping.syntheticToc,
      spineItems: mapping.sections.map((s) => ({
        id: s.sectionId,
        characterCount: s.characterCount,
        index: s.playOrder,
      })),
    },
    sections: mapping.sections,
    inventory: {
      bookId,
      title: shared.title,
      author: shared.author,
      addedAt: now,
      sourceFilename: file.name,
      tags: [],
      status: 'unread',
      lastInteraction: now,
      coverPalette: shared.coverPalette,
      perceptualPalette: shared.perceptualPalette,
      language: shared.language,
    },
    progress: { bookId, percentage: 0, lastRead: 0, completedRanges: [] },
    overrides: { bookId, lexicon: [] },
    readingListEntry: {
      filename: file.name,
      title: shared.title,
      author: shared.author,
      isbn: undefined,
      percentage: 0,
      lastUpdated: now,
      status: 'to-read',
      rating: undefined,
    },
    ttsContentBatches: mapping.ttsContentBatches,
    tableBatches: mapping.tableBatches,
    searchText: {
      extractionVersion: TTS_EXTRACTION_VERSION,
      sections: mapping.searchSections,
    },
  };
}
